import { useEffect, useMemo, useRef } from "react";
import { ThicknessBottle } from "../types";
import { formatAppDateTime } from "../utils/dateTime";

export type ThicknessSortState = {
  field: "timestamp" | "trial_name" | "relative_bottle_id";
  direction: "asc" | "desc";
};

type Props = {
  data: ThicknessBottle[];
  selectedIds: Set<number>;
  onToggle: (id: number) => void;
  onSelectAll: (ids: number[]) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  sort: ThicknessSortState;
  onSortChange: (sort: ThicknessSortState) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
};

export default function ThicknessTable({
  data,
  selectedIds,
  onToggle,
  onSelectAll,
  page,
  pageSize,
  onPageChange,
  sort,
  onSortChange,
  searchTerm,
  onSearchChange
}: Props) {
  const start = page * pageSize;
  const end = start + pageSize;
  const paginated = useMemo(() => data.slice(start, end), [data, start, end]);
  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));

  const masterRef = useRef<HTMLInputElement>(null);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const allIds = data.map((p) => p.id);
  const allChecked = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
  const someChecked = selectedIds.size > 0 && !allChecked;

  useEffect(() => {
    if (masterRef.current) {
      masterRef.current.indeterminate = someChecked;
    }
  }, [someChecked]);

  useEffect(() => {
    const el = tbodyRef.current;
    if (!el) return;
    el.classList.remove("table-refresh");
    // Force reflow so the animation can restart on every change
    void el.offsetWidth;
    el.classList.add("table-refresh");
  }, [paginated]);

  const toggleSort = (field: ThicknessSortState["field"]) => {
    const isSame = sort.field === field;
    const direction = isSame && sort.direction === "asc" ? "desc" : "asc";
    onSortChange({ field, direction });
  };

  return (
    <div className="card">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 10,
          alignItems: "center",
          flexWrap: "wrap"
        }}
      >
        <div>
          <div className="label">Search</div>
          <input
            className="input"
            placeholder="Date/time, trial name, bottle id"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        <div className="pill">Records: {data.length}</div>
        <div className="pill">Selected: {selectedIds.size}</div>
      </div>

      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>
                <input
                  ref={masterRef}
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => onSelectAll(e.target.checked ? allIds : [])}
                />
              </th>
              <SortableHeader label="Timestamp" field="timestamp" sort={sort} onToggle={toggleSort} />
              <SortableHeader label="Trial Name" field="trial_name" sort={sort} onToggle={toggleSort} />
              <SortableHeader
                label="Bottle ID"
                field="relative_bottle_id"
                sort={sort}
                onToggle={toggleSort}
              />
            </tr>
          </thead>
          <tbody ref={tbodyRef}>
            {paginated.map((row) => (
              <tr
                key={row.id}
                style={{
                  background: selectedIds.has(row.id) ? "rgba(46,199,166,0.08)" : "transparent"
                }}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(row.id)}
                    onChange={() => onToggle(row.id)}
                  />
                </td>
                <td>{formatAppDateTime(row.timestamp)}</td>
                <td>{row.trial_name}</td>
                <td>{row.relative_bottle_id}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
        <button
          className="button secondary"
          disabled={page === 0}
          onClick={() => onPageChange(Math.max(0, page - 1))}
        >
          Prev
        </button>
        <div className="pill">
          Page {page + 1} / {totalPages}
        </div>
        <button
          className="button secondary"
          disabled={page + 1 >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function SortableHeader({
  label,
  field,
  sort,
  onToggle
}: {
  label: string;
  field: ThicknessSortState["field"];
  sort: ThicknessSortState;
  onToggle: (field: ThicknessSortState["field"]) => void;
}) {
  const isActive = sort.field === field;
  return (
    <th style={{ cursor: "pointer" }} onClick={() => onToggle(field)}>
      {label} {isActive ? (sort.direction === "asc" ? "▲" : "▼") : ""}
    </th>
  );
}
